/**
 * DOCloud Dashboard
 */

import React, { useState, useEffect } from 'react';
import intl from 'react-intl-universal';
import { signOut } from 'aws-amplify/auth';
// react-router components
import { useLocation, useOutletContext, Outlet } from 'react-router-dom';
import KrillChat from 'krill';
import logger from 'utils/logger';

// @mui material components
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// components
import Sidenav from 'views/Dashboard/Sidenav';
import DashboardLayout from 'components/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'components/Navbar';
import ServiceTermsPopUpWindow from 'components/ServiceTermsPopUpWindow';
import UploadMissionsMenu from 'components/UploadMissions';
import theme from 'assets/theme';
import themeDark from 'assets/theme-dark';
import { getStaticMenuContents } from 'views/root/routes';
import localData from 'utils/localData';
import { initLocale, queryLocalCode } from 'utils/intl';
import { PageLoader } from 'components';
import { useMaterialUIController } from 'context';
import { UploadMissionContextControllerProvider } from 'context/NewIssueContext';
import { ConfirmModal } from 'shared/modals/confirmModal';
import { AgreeServiceTermsModal } from './styles';

// Images
import brandWhite from 'assets/images/logos/iwg/iwg-white-logo-cropped.png';
import brandDark from 'assets/images/logos/iwg/iwg-dark-logo-cropped.png';
import { LOGOUT_URL } from 'shared/constants/apis';
import { USER_DATA } from 'shared/constants/users';
import { getStoredAuthToken } from 'utils/authToken';

const WINDOW_BREAKPOINT = 1250;

// Create a logger instance for this component
const log = logger.createLogger('Dashboard');

export default function App() {
  const [controller] = useMaterialUIController();
  const { currentUserInfo } = useOutletContext();
  const [openDrawer, setOpenDrawer] = useState(true);
  const { sidenavColor, transparentSidenav, whiteSidenav, darkMode } =
    controller;
  const [currentRoutes, setCurrentRoutes] = useState([]);
  const [showConfirmBeforeLogout, setShowConfirmBeforeLogout] = useState(false);
  const { pathname } = useLocation();

  const checkIfKrillChatIsInitialized = () => {
    return userInfo.token && userInfo.token !== 'no_token';
  };

  // Add this useEffect for handling translations
  useEffect(() => {
    const initializeLocale = async () => {
      await initLocale(
        queryLocalCode(localData.get(USER_DATA.PREFFERED_LANGUAGE))
      );
    };
    initializeLocale();
  }, []); // Em

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;

    const updateWindowDimensions = () => {
      const newWidth = window.innerWidth;
      if (newWidth < WINDOW_BREAKPOINT) setOpenDrawer(false);
      else setOpenDrawer(true);
    };

    window.addEventListener('resize', updateWindowDimensions);

    return () => window.removeEventListener('resize', updateWindowDimensions);
  }, [pathname]);

  useEffect(() => {
    if (currentUserInfo) {
      const staticRoutes = getStaticMenuContents();
      setCurrentRoutes(staticRoutes);
    }
  }, [currentUserInfo]);

  if (!currentUserInfo) return <PageLoader />;

  const userInfo = { ...currentUserInfo };

  if (checkIfKrillChatIsInitialized) {
    KrillChat.Config.setStoredUser2(
      userInfo.username,
      userInfo.token,
      userInfo.username,
      getStoredAuthToken()
    );
    KrillChat.Config.setConfig({
      CURRENT_LANGUAGE: localData.get(USER_DATA.PREFFERED_LANGUAGE),
    });
  } else {
    log.error('No token found, KrillChat will not be able to set the user');
  }

  const handleClickLogout = () => {
    setShowConfirmBeforeLogout(true);
  };

  const LogoutModal = () => {
    const [isSigningOut, setIsSigningOut] = useState(false);
    const context = KrillChat.Context.useKrillChatContext();

    const handleSignOut = async () => {
      setIsSigningOut(true);
      if (
        import.meta.env.VITE_AUTH_SERVER === 'aws-cognito' &&
        import.meta.env.VITE_SIGN_IN_PAGE === 'aws-custom'
      ) {
        // Sign out from KrillChat
        try {
          await context.logout();
          // setShowKrillChat(false);
        } catch (error) {
          log.error('Error signing out:', error);
        }
        // Sign out from AWS Cognito
        try {
          await signOut({
            global: true, // Sign out from all devices
          });
          // Clear local storage
          localData.clear();

          // Redirect to logout URL
          window.location.assign(
            import.meta.env.VITE_LOGOUT_REDIRECT_URL || LOGOUT_URL
          );
        } catch (error) {
          log.error('Error signing out:', error);
          setIsSigningOut(false);
        }
      }
    };

    return (
      <ConfirmModal
        isOpened={showConfirmBeforeLogout}
        title={intl.get('dashboard_logout_modal_text_confirm_title')}
        bodyText={intl.get('dashboard_logout_modal_text_confirm_hint')}
        onConfirm={handleSignOut}
        onCancel={() => setShowConfirmBeforeLogout(false)}
        confirmButtonText={
          isSigningOut
            ? intl.get('dashboard_logout_modal_button_logging_out')
            : intl.get('dashboard_logout_modal_button_logout')
        }
        cancelButtonText={intl.get('dashboard_logout_modal_button_cancel')}
        confirmButtonIsLoadingButton={true}
        confirmButtonIsLoading={isSigningOut}
      />
    );
  };

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <KrillChat.Provider>
        <CssBaseline />
        <div
          data-testid="dashboard-div"
          style={{
            overflowY: 'visible',
          }}>
          <DashboardNavbar
            onOpenDrawer={() => setOpenDrawer(!openDrawer)}
            brand={
              (transparentSidenav && !darkMode) || whiteSidenav
                ? brandDark
                : brandWhite
            }
          />

          <Sidenav
            openDrawer={openDrawer}
            color={sidenavColor}
            routes={currentRoutes}
            userInfo={userInfo}
            handleClickLogout={handleClickLogout}
          />

          <UploadMissionContextControllerProvider>
            <DashboardLayout openDrawer={openDrawer}>
              {currentUserInfo.agreedServiceTerms && (
                <Outlet context={{ userInfo }} />
              )}
              <UploadMissionsMenu />
              {/* Irregular modal */}
              <AgreeServiceTermsModal
                open={!currentUserInfo.agreedServiceTerms}>
                <>
                  <ServiceTermsPopUpWindow />
                </>
              </AgreeServiceTermsModal>
              <LogoutModal />
            </DashboardLayout>
          </UploadMissionContextControllerProvider>
          {checkIfKrillChatIsInitialized() && (
            <KrillChat.UI.Widget.View settings={{}} />
          )}
        </div>
      </KrillChat.Provider>
    </ThemeProvider>
  );
}
